import { Link } from 'react-router-dom';
import { ClipboardList, ExternalLink, Play } from 'lucide-react';
import { useActiveTournament } from '../state/TournamentContext';
import { useActiveRole } from '../state/RoleContext';
import { useStore } from '../hooks/useStore';
import { getLiveScore, getResultForGame } from '../services/scoringService';
import type { AppDatabase } from '../services/store';

/**
 * Assigned game queue for scorekeepers and timekeepers. This is the non-admin
 * entry point into the same scoring console used by League HQ.
 */
export function EventMyGamesPage() {
  const { tournament, games, teams } = useActiveTournament();
  const { profile } = useActiveRole();
  const db = useStore((d) => d);

  const myAssignments = db.gameAssignments.filter(
    (assignment) =>
      assignment.profileId === profile.id &&
      assignment.status !== 'declined' &&
      games.some((game) => game.id === assignment.gameId),
  );
  const myGames = myAssignments
    .map((assignment) => ({ assignment, game: games.find((game) => game.id === assignment.gameId)! }))
    .filter((item) => Boolean(item.game))
    .sort((a, b) => startOf(a.game.id, db).localeCompare(startOf(b.game.id, db)));

  const teamById = new Map(teams.map((team) => [team.id, team]));

  if (myGames.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <ClipboardList size={22} />
          <p>No assigned games yet.</p>
          <p className="muted">When an admin assigns you as a scorekeeper or timekeeper, games will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title">My scoring queue</h3>
          <p className="card__sub">
            {myGames.length} assignment{myGames.length === 1 ? '' : 's'} for {tournament.name}.
          </p>
        </div>
      </div>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Start</th>
              <th>Match</th>
              <th>Role</th>
              <th>Status</th>
              <th>Score</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {myGames.map(({ assignment, game }) => {
              const start = startOf(game.id, db);
              const result = getResultForGame(game.id);
              const live = game.status === 'in_progress' ? getLiveScore(game.id) : null;
              return (
                <tr key={assignment.id}>
                  <td className="muted">{start ? new Date(start).toLocaleString() : '-'}</td>
                  <td>
                    <span style={{ fontWeight: 700 }}>{teamById.get(game.homeTeamId)?.name ?? '?'}</span>
                    {' vs '}
                    <span style={{ fontWeight: 700 }}>{teamById.get(game.awayTeamId)?.name ?? '?'}</span>
                    {game.displayLabel && <div className="muted" style={{ fontSize: 12 }}>{game.displayLabel}</div>}
                  </td>
                  <td className="muted">{assignment.position.replace('_', ' ')}</td>
                  <td>
                    <span className={`chip chip--${game.status === 'in_progress' ? 'ok' : game.status === 'final' || game.status === 'forfeit' ? 'info' : 'warn'}`}>
                      <span className="chip__dot" />
                      {game.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="muted">
                    {result ? `${result.homeScore} - ${result.awayScore}` : live ? `${live.home} - ${live.away}` : '-'}
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                      <Link to={`/events/${tournament.slug}/games/${game.id}`} className="btn btn--ghost btn--sm">
                        <ExternalLink size={12} /> Public
                      </Link>
                      <Link to={`/events/${tournament.slug}/my-games/${game.id}/score`} className="btn btn--ghost btn--sm">
                        <Play size={12} /> Score
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function startOf(gameId: string, db: AppDatabase) {
  const game = db.games.find((row) => row.id === gameId);
  if (!game?.scheduleSlotId) return '';
  return db.scheduleSlots.find((slot) => slot.id === game.scheduleSlotId)?.startsAt ?? '';
}
